/**
 * FartSelectionHUD - Shows current fart type and ammo on desktop
 * 
 * Displays:
 * - Current fart type icon and name
 * - Ammo count (∞ for basic)
 * - All available types with keyboard shortcuts
 */

import { FartType, FART_CONFIGS } from '../entities/farts/FartTypes';
import { FartInventory } from '../entities/farts/FartInventory';

export class FartSelectionHUD {
    private container: HTMLDivElement;
    private inventory: FartInventory;
    private styleElement: HTMLStyleElement;

    constructor(inventory: FartInventory) {
        this.inventory = inventory;
        this.container = document.createElement('div');
        this.styleElement = document.createElement('style');
        this.createStyles();
        this.createHUD();
    }

    private createStyles(): void {
        this.styleElement.textContent = `
            .fart-selection-hud {
                position: fixed;
                bottom: 20px;
                left: 20px;
                display: flex;
                gap: 8px;
                z-index: 1000;
                pointer-events: none;
            }
            
            .fart-slot {
                width: 48px;
                height: 48px;
                border-radius: 8px;
                background: rgba(0, 0, 0, 0.6);
                border: 2px solid rgba(255, 255, 255, 0.3);
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                position: relative;
                transition: all 0.15s ease;
            }
            
            .fart-slot.selected {
                border-color: #f1c40f;
                background: rgba(241, 196, 15, 0.3);
                transform: scale(1.1);
            }
            
            .fart-slot.empty {
                opacity: 0.4;
            }
            
            .fart-slot .icon {
                font-size: 20px;
                line-height: 1;
            }
            
            .fart-slot .key {
                position: absolute;
                top: -8px;
                left: 50%;
                transform: translateX(-50%);
                background: rgba(0, 0, 0, 0.8);
                color: white;
                font-size: 10px;
                font-family: Arial, sans-serif;
                padding: 1px 4px;
                border-radius: 3px;
            }
            
            .fart-slot .ammo {
                position: absolute;
                bottom: -6px;
                right: -6px;
                min-width: 18px;
                height: 18px;
                background: #e74c3c;
                color: white;
                font-size: 10px;
                font-weight: bold;
                font-family: Arial, sans-serif;
                display: flex;
                justify-content: center;
                align-items: center;
                border-radius: 9px;
                padding: 0 4px;
            }
            
            .fart-slot .ammo.unlimited {
                background: #27ae60;
            }
        `;
        document.head.appendChild(this.styleElement);
    }

    private createHUD(): void {
        this.container.className = 'fart-selection-hud';

        const types: FartType[] = [
            FartType.BASIC,
            FartType.LINGERING,
            FartType.GHOST,
            FartType.EXPLOSIVE,
            FartType.SBD,
            FartType.PROPULSION
        ];

        types.forEach((type, index) => {
            const slot = document.createElement('div');
            slot.className = 'fart-slot';
            slot.dataset.type = type;

            const config = FART_CONFIGS[type];

            // Key label
            const key = document.createElement('div');
            key.className = 'key';
            key.textContent = (index + 1).toString();

            // Icon
            const icon = document.createElement('div');
            icon.className = 'icon';
            icon.textContent = config.icon;

            // Ammo
            const ammo = document.createElement('div');
            ammo.className = 'ammo';
            ammo.dataset.ammoFor = type;

            slot.appendChild(key);
            slot.appendChild(icon);
            slot.appendChild(ammo);
            this.container.appendChild(slot);
        });

        document.body.appendChild(this.container);
        this.update();
    }

    /**
     * Update the HUD display
     */
    update(): void {
        const selectedType = this.inventory.getSelectedType();

        const slots = this.container.querySelectorAll('.fart-slot');
        slots.forEach((slot) => {
            const type = (slot as HTMLElement).dataset.type as FartType;
            const ammoEl = slot.querySelector('.ammo') as HTMLElement;

            // Update selection
            slot.classList.toggle('selected', type === selectedType);

            // Update ammo
            if (type === FartType.BASIC) {
                ammoEl.textContent = '∞';
                ammoEl.className = 'ammo unlimited';
                slot.classList.remove('empty');
            } else {
                const ammoCount = this.inventory.getAmmo(type);
                ammoEl.textContent = ammoCount.toString();
                ammoEl.className = 'ammo';
                slot.classList.toggle('empty', ammoCount === 0);
            }
        });
    }

    /**
     * Clean up
     */
    destroy(): void {
        this.container.remove();
        this.styleElement.remove();
    }
}
